import { createContext, useContext, ReactNode, useCallback } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { SubscriptionLimits } from '@/types/subscription';
import { subscriptionService } from '@/services/api';
import { useAuth } from '@/contexts/auth-context';

interface SubscriptionContextType {
  limits: SubscriptionLimits | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isFeatureEnabled: (feature: keyof SubscriptionLimits['features']) => boolean;
  isAtLimit: (resource: keyof SubscriptionLimits['usage']) => boolean;
  canPerformAction: (resource: keyof SubscriptionLimits['usage']) => boolean;
  getUsagePercentage: (resource: keyof SubscriptionLimits['usage']) => number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Fetch subscription limits using React Query
  const {
    data: limits,
    isLoading,
    isError,
    error,
    refetch,
  }: UseQueryResult<SubscriptionLimits, Error> = useQuery({
    // the account is in the key: the provider stays mounted across sign-in, sign-out and impersonation, so another account's limits are never the cached answer
    // (invalidations by ['subscription', 'limits'] still match: a key prefix)
    queryKey: ['subscription', 'limits', user?.id ?? null, user?.organizationId ?? null],
    queryFn: () => subscriptionService.getSubscriptionLimits(),
    enabled: isAuthenticated && !authLoading && user?.role !== 'crew' && user?.role !== 'estimator', // Only fetch when authenticated; crew and estimators are refused the limits
    staleTime: 0, // Always refetch - subscription can change externally via Stripe portal
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true, // Refetch when user returns from Stripe portal
  });

  // The server enforces every plan limit (403 subscription_limit, 422 too_many_teammates, with its own message), so
  // these checks only block when the limits are KNOWN to say no. While the limits load, or when the request failed
  // (staging answered 503 and every owner got "Choose a plan" instead of the invite form), they allow.

  /**
   * Check if a specific feature is enabled for the current subscription (unknown limits: enabled)
   */
  const isFeatureEnabled = useCallback(
    (feature: keyof SubscriptionLimits['features']): boolean => {
      if (!limits?.features) return true;
      return limits.features[feature] !== false;
    },
    [limits]
  );

  /**
   * Check if a resource has reached its limit (100%). Unknown or unlimited: not at the limit.
   */
  const isAtLimit = useCallback(
    (resource: keyof SubscriptionLimits['usage']): boolean => {
      const usage = limits?.usage?.[resource];
      if (!usage || usage.unlimited || typeof usage.limit !== 'number') return false;
      return usage.current >= usage.limit;
    },
    [limits]
  );

  /**
   * Check if an action can be performed (not at limit)
   */
  const canPerformAction = useCallback(
    (resource: keyof SubscriptionLimits['usage']): boolean => {
      return !isAtLimit(resource);
    },
    [isAtLimit]
  );

  /**
   * Get the usage percentage for a specific resource
   */
  const getUsagePercentage = useCallback(
    (resource: keyof SubscriptionLimits['usage']): number => {
      return limits?.usage?.[resource]?.percentage ?? 0;
    },
    [limits]
  );

  const value: SubscriptionContextType = {
    limits,
    isLoading,
    isError,
    error,
    refetch: () => {
      refetch();
    },
    isFeatureEnabled,
    isAtLimit,
    canPerformAction,
    getUsagePercentage,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

/**
 * Hook to check if a feature is available before performing an action
 * Provides both the check and helper to show upgrade dialog
 */
export function useFeatureCheck() {
  const { isFeatureEnabled, isAtLimit, canPerformAction } = useSubscription();

  return {
    isFeatureEnabled,
    isAtLimit,
    canPerformAction,
  };
}
